import { useContext, useEffect, useState } from 'react';
import _ from 'lodash';
import Highcharts from 'highcharts';

import AccountContext from 'contexts/AccountContext';
import ChartColorsContext from 'contexts/ChartColorsContext';
import useMetricMatrix from 'hooks/useMetricMatrix';
import getGroupByField from 'hooks/useMetricMatrix/getGroupByField';
import useChartDrillDowns from 'hooks/useChartDrillDowns';
import useMetric from 'hooks/useMetric';
import useValueFormatters from 'hooks/useValueFormatters';
import { DEFAULT_FORMATTING } from '../../../constants';
import stackedBar from '../highchartOptions/stackedBarOptions';
import {
  buildCategories,
  buildCategoriesAlt,
  buildStackedSeries,
  buildStackedSeriesAlt,
} from '../stackedFormatters';
import toAutoInterval from '../toAutoInterval';

const useStackedBarData = ({
  chartDefinition,
  chartRef,
}: {
  chartDefinition: V5ChartDefinition;
  chartRef: React.MutableRefObject<any>;
}): {
  options: Highcharts.Options | undefined;
  groupKey: string | undefined;
} => {
  const { unitsLocale } = useContext(AccountContext);
  const { getChartColor, getChartColorIndex } = useContext(ChartColorsContext);

  const [series, setSeries] = useState<StackedSeries[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [termLookup, setTermLookup] = useState<
    { [term: string]: string } | undefined
  >();
  const [autoInterval, setAutoInterval] = useState<AutoInterval | undefined>();
  const [groupKey] = useState(getGroupByField(chartDefinition));
  const [options, setOptions] = useState<Highcharts.Options | undefined>();

  const { formatMetric } = useValueFormatters();
  const { isLoading, rowData } = useMetricMatrix({
    chartDef: chartDefinition,
    skip2DRowDataFormatting: true,
  });
  const metric = useMetric(chartDefinition.series[0].metricId);
  const { onDrillDown, syncChart } = useChartDrillDowns(
    chartDefinition.dimensionA ? chartDefinition.dimensionA.field : '',
    chartRef,
    'stackedBar',
    chartDefinition,
    autoInterval,
  );

  useEffect(() => {
    syncChart();
  }, [syncChart]);

  useEffect(() => {
    if (!rowData) {
      return;
    }
    if (chartDefinition.trendByCalendarInterval !== 'auto') {
      return;
    }
    try {
      // @ts-ignore
      const aI = Object.keys(rowData[0])['interval'] as string;
      const newAI = toAutoInterval(aI);
      setAutoInterval((currentAi) =>
        _.isEqual(currentAi, newAI) ? currentAi : newAI,
      );
    } catch (ex) {
      return undefined;
    }
  }, [chartDefinition.trendByCalendarInterval, rowData]);

  useEffect(() => {
    const altPivot = chartDefinition.stackedBarOptions
      ? chartDefinition.stackedBarOptions.altPivot
      : false;

    if (!metric) {
      return;
    }

    if (!isLoading) {
      if (altPivot) {
        setSeries(
          buildStackedSeriesAlt({
            rowData,
            groupKey,
            chartDef: chartDefinition,
            getChartColor,
            getChartColorIndex,
            autoInterval,
          }),
        );
        const newCategories = buildCategoriesAlt(rowData, groupKey);
        setCategories(newCategories);
        // The categories are the terms themselves. The options wait for a
        // lookup, so without one a pivoted chart never draws.
        setTermLookup(_.fromPairs(newCategories.map((c) => [c, c])));
      } else {
        const { prettyDates: newCategories, termLookup: newTermLookup } =
          buildCategories(rowData, groupKey, chartDefinition, autoInterval);
        setSeries(
          buildStackedSeries({
            rowData,
            groupKey,
            newCategories,
            newTermLookup,
            getChartColor,
            getChartColorIndex,
            metricId: metric.id,
          }),
        );
        setCategories(newCategories);
        setTermLookup(newTermLookup);
      }
    }
  }, [
    groupKey,
    rowData,
    isLoading,
    chartDefinition,
    autoInterval,
    metric,
    getChartColor,
    getChartColorIndex,
  ]);

  useEffect(() => {
    const direction = chartDefinition.stackedBarOptions
      ? chartDefinition.stackedBarOptions.direction
      : 'horizontal';
    const asPercentage = chartDefinition.stackedBarOptions
      ? chartDefinition.stackedBarOptions.asPercentage
      : false;

    const xAxis = {
      categories,
      labels: { style: { fontSize: 11 } },
    };

    if (!termLookup) {
      return;
    }

    const formatting = metric ? metric.formatting : DEFAULT_FORMATTING;
    const { precision, postfix, prefix, positiveDeltaIsGood } = formatting;

    const newOptions = stackedBar({
      formatMetric,
      title: ' ',
      series: series.map((s) => ({
        ...s,
        onDrillDown,
        termLookup,
        precision,
        postfix,
        prefix,
        positiveDeltaIsGood,
        animation: false,
      })),
      xAxis,
      direction,
      asPercentage,
      fontSize: 11,
    });
    setOptions(newOptions as any as Highcharts.Options);
  }, [
    autoInterval,
    categories,
    chartDefinition,
    formatMetric,
    groupKey,
    isLoading,
    metric,
    onDrillDown,
    rowData,
    series,
    termLookup,
    unitsLocale,
  ]);

  return { options, groupKey };
};

export default useStackedBarData;
